<script setup>
import { vOnClickOutside } from '@vueuse/components';

defineProps({
  show: {
    type: Boolean,
    required: true,
  },
  size: {
    type: String,
    default: 'md', // sm, md, lg, xl
    validator: value => ['sm', 'md', 'lg', 'xl'].includes(value),
  },
});

const emit = defineEmits(['close']);

const sizeClasses = {
  sm: 'max-w-md',
  md: 'max-w-2xl',
  lg: 'max-w-4xl',
  xl: 'max-w-6xl',
};

const handleClose = () => {
  emit('close');
};

// Hooks pour animations personnalisées
const onBeforeEnter = el => {
  el.style.transformOrigin = 'right center';
};

const onEnter = (el, done) => {
  // Force reflow pour déclencher l'animation
  // eslint-disable-next-line no-unused-expressions
  el.offsetHeight;
  done();
};

const onAfterEnter = el => {
  el.style.transformOrigin = '';
};

const onBeforeLeave = el => {
  el.style.transformOrigin = 'right center';
};

const onLeave = (el, done) => {
  done();
};
</script>

<template>
  <!-- Overlay avec transition fade améliorée -->
  <Transition
    enter-active-class="transition-opacity duration-300 ease-out"
    leave-active-class="transition-opacity duration-250 ease-in"
    enter-from-class="opacity-0"
    enter-to-class="opacity-100"
    leave-from-class="opacity-100"
    leave-to-class="opacity-0"
  >
    <div
      v-if="show"
      class="fixed inset-0 bg-black/40 backdrop-blur-sm z-[60]"
      @click="handleClose"
    />
  </Transition>

  <!-- Drawer Container -->
  <div
    v-if="show"
    class="fixed top-0 ltr:right-0 rtl:left-0 h-full z-[70] w-full flex justify-end pointer-events-none"
    :class="sizeClasses[size]"
  >
    <!-- Transition accordéon horizontale améliorée -->
    <Transition
      enter-active-class="transition-all duration-[400ms]"
      leave-active-class="transition-all duration-[320ms]"
      enter-from-class="ltr:translate-x-full rtl:-translate-x-full opacity-0 scale-95"
      enter-to-class="translate-x-0 opacity-100 scale-100"
      leave-from-class="translate-x-0 opacity-100 scale-100"
      leave-to-class="ltr:translate-x-full rtl:-translate-x-full opacity-0 scale-98"
      @before-enter="onBeforeEnter"
      @enter="onEnter"
      @after-enter="onAfterEnter"
      @before-leave="onBeforeLeave"
      @leave="onLeave"
    >
      <div
        v-if="show"
        v-on-click-outside="[
          handleClose,
          { ignore: ['.ignore-outside-click'] },
        ]"
        class="h-full bg-n-solid-2 shadow-2xl flex flex-col w-full border-l border-n-weak pointer-events-auto transform-gpu will-change-transform"
      >
        <slot />
      </div>
    </Transition>
  </div>
</template>

<style scoped>
/* Animation avec cubic-bezier pour un effet accordéon fluide */
.transition-all {
  transition-timing-function: cubic-bezier(
    0.34,
    1.56,
    0.64,
    1
  ); /* Spring-like easing pour l'entrée */
}

[class*='leave-active'] {
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1); /* Smooth exit */
}

/* Optimisation GPU pour des animations fluides */
.transform-gpu {
  transform: translateZ(0);
  backface-visibility: hidden;
  perspective: 1000px;
}

/* Animation de l'ombre au survol */
.shadow-2xl {
  box-shadow:
    0 25px 50px -12px rgba(0, 0, 0, 0.25),
    0 0 0 1px rgba(0, 0, 0, 0.05);
  transition: box-shadow 300ms ease-out;
}
</style>
